import type { InputDefinition, ParameterDefinition } from '../universal-node-schema'

/**
 * The entry window: when an entry may happen, set on the entry node itself
 * instead of built as a Time Signal ANDed into the trigger with a Merge.
 *
 * Two ways to set it, like a Blender socket: type the times into the node's
 * From / Until fields, or wire any boolean (a Time Signal, or something
 * richer) into the Window socket. A wire wins over the fields, which hide
 * while it is connected.
 *
 * The engine never sees any of this. `buildEngineStrategyGraph` expands the
 * window into the Time Signal and Merge a user would have built by hand
 * (utils/entry-window.ts), so every engine path gets it for free.
 *
 * Lives on the two nodes people think of as "the entry": Simple Trade, and
 * Entry Rules (standalone, or inside an Advanced Trade, where it limits the
 * trade's trigger). Day-of-week is the next field to join it.
 */

export const ENTRY_WINDOW_HANDLE = 'window'

export const ENTRY_WINDOW_PARAMS = ['entryWindow', 'entryWindowStart', 'entryWindowEnd'] as const

/** The clock times the session presets offer on every time field that bounds a window. */
export const SESSION_TIME_PRESETS = [
  { label: '04:00', value: '04:00' },
  { label: '09:30', value: '09:30' },
  { label: '12:00', value: '12:00' },
  { label: '14:00', value: '14:00' },
  { label: '16:00', value: '16:00' },
  { label: '20:00', value: '20:00' },
]

export const entryWindowInput: InputDefinition = {
  id: ENTRY_WINDOW_HANDLE,
  displayName: 'Window',
  acceptedTypes: ['boolean'],
  acceptedSources: ['*'],
  optional: true,
}

const whileWindowOn = { parameter: 'entryWindow', values: [true] }

export const entryWindowParameters: ParameterDefinition[] = [
  {
    name: 'entryWindow',
    displayName: 'Entry Window',
    description: 'Only enter between the From and Until times (ET).',
    type: 'boolean',
    default: false,
    hiddenWhenConnected: ENTRY_WINDOW_HANDLE,
  },
  {
    name: 'entryWindowStart',
    displayName: 'From',
    type: 'time',
    default: '09:30',
    timePresets: SESSION_TIME_PRESETS,
    dependsOn: whileWindowOn,
    hiddenWhenConnected: ENTRY_WINDOW_HANDLE,
  },
  {
    name: 'entryWindowEnd',
    displayName: 'Until',
    type: 'time',
    default: '16:00',
    timePresets: SESSION_TIME_PRESETS,
    dependsOn: whileWindowOn,
    hiddenWhenConnected: ENTRY_WINDOW_HANDLE,
  },
]
